import { useEffect, useMemo, useRef } from 'react';
import { useQuery, skipToken } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';

import { ENV_BASE_URL } from 'env';
import { CertificateEditDocument, ITemplate } from 'graphql/types';
import { CertificatePreviewModal } from 'certificate/components/CertificatePreviewModal';
import { CERTIFICATE_TEMPLATE_DATA } from 'certificate/constants/certificate';
import { ICertificatePreviewSource } from 'certificate/types';

const IFRAME_NAME = 'certificateTypePreviewFrame';
const PREVIEW_URL = `${ENV_BASE_URL}/certificates/preview/`;

interface IProps {
    /**
     * The certificate's preview fields. When provided, they are used directly
     * and no request is made. Callers that already hold these fields (e.g. the
     * edit form's live values) should pass them to avoid a redundant fetch.
     */
    certificate?: ICertificatePreviewSource;
    /** Fetched and used only when `certificate` is not provided. */
    certificateId?: string;
    open: boolean;
    onClose(): void;
}

/**
 * Opens the certificate template preview modal, POSTing the template fields to
 * the preview iframe endpoint. The fields come either from a passed-in
 * `certificate` object or, as a fallback, from fetching a saved certificate by
 * its `certificateId`.
 */
export const CertificateTypePreviewModal = ({
    certificate,
    certificateId,
    open,
    onClose,
}: IProps) => {
    const [translate] = useTranslation();
    const formRef = useRef<HTMLFormElement>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    const { data } = useQuery(
        CertificateEditDocument,
        open && certificateId && !certificate
            ? { variables: { id: certificateId } }
            : skipToken
    );

    const source = certificate ?? data?.certificate;

    const postPayload = useMemo(() => {
        if (!source) return {};

        const {
            template,
            primaryColor,
            secondaryColor,
            title,
            subtitle,
            text,
            signatureFile,
            expiry,
            customTemplate,
        } = source;

        return {
            template: template
                ? CERTIFICATE_TEMPLATE_DATA[template]
                : undefined,
            color1: !!primaryColor ? `#${primaryColor}` : undefined,
            color2: !!secondaryColor ? `#${secondaryColor}` : undefined,
            title: title ?? undefined,
            subtitle: subtitle ?? undefined,
            text: text ?? undefined,
            signature: signatureFile?.url ?? undefined,
            expiry: expiry ?? undefined,
            custom_template_file_id:
                template === ITemplate.Custom && !!customTemplate?.id
                    ? customTemplate.id
                    : undefined,
        };
    }, [source]);

    // Wait until both the iframe (rendered by the modal) and the form (rendered
    // here) are mounted before submitting, otherwise the POST target would not
    // resolve to the iframe.
    useEffect(() => {
        if (!open || !source) return;

        let animationFrame: number;

        const submitWhenReady = () => {
            const iframe = iframeRef.current;
            const form = formRef.current;

            if (iframe && form) {
                form.submit();

                return;
            }

            animationFrame = requestAnimationFrame(submitWhenReady);
        };

        animationFrame = requestAnimationFrame(submitWhenReady);

        return () => cancelAnimationFrame(animationFrame);
    }, [open, source]);

    return (
        <>
            <form
                action={PREVIEW_URL}
                method="post"
                ref={formRef}
                style={{ display: 'none' }}
                target={IFRAME_NAME}
            >
                {Object.entries(postPayload)
                    .filter(([, value]) => !!value)
                    .map(([name, value]) => (
                        <input
                            key={name}
                            name={name}
                            type="hidden"
                            value={value}
                        />
                    ))}
            </form>

            <CertificatePreviewModal
                iframeProps={{
                    name: IFRAME_NAME,
                    title: translate('preview'),
                }}
                iframeRef={iframeRef}
                open={open}
                onClose={onClose}
            />
        </>
    );
};
